/**
 * OWASP A01:2021 — Unvalidated Redirect / Forward protection.
 *
 * All client-side redirects MUST pass through this module so the destination
 * host is validated against an allowlist. For the trax / app.tempcomputing.com
 * deployment, only tempcomputing.com (and its subdomains for tenant routing)
 * are permitted by default.
 *
 * The allowlist is configurable via the REACT_APP_ALLOWED_REDIRECT_HOSTS
 * environment variable (comma-separated host suffixes). This keeps the
 * security control identical across branches; only the allowlist value
 * differs per deployment.
 */

// Hosts allowed by default when no env override is provided. Each entry is
// treated as a host suffix, so "tempcomputing.com" matches both the apex
// and any subdomain like "acme.tempcomputing.com".
const DEFAULT_ALLOWED_HOST_SUFFIXES = ['tempcomputing.com'];

// Hosts that are always allowed for local development regardless of env.
const ALWAYS_ALLOWED_DEV_HOSTS = ['localhost', '127.0.0.1'];

const parseEnvList = (raw) =>
  String(raw || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

const ALLOWED_HOST_SUFFIXES = (() => {
  const fromEnv = parseEnvList(process.env.REACT_APP_ALLOWED_REDIRECT_HOSTS);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_ALLOWED_HOST_SUFFIXES;
})();

const hostnameMatchesSuffix = (hostname, suffix) => {
  if (!hostname || !suffix) return false;
  const h = hostname.toLowerCase();
  const s = suffix.toLowerCase();
  return h === s || h.endsWith('.' + s);
};

/**
 * Validate that a redirect target is safe (same-origin path or an allowlisted host).
 *
 * Accepted:
 *  - Absolute same-origin paths starting with "/" (but not "//" — protocol-relative)
 *  - https:// URLs whose hostname matches an allowlisted suffix
 *  - http:// or any protocol on localhost / 127.0.0.1 (dev only)
 *
 * Rejected:
 *  - Empty / non-string input
 *  - Protocol-relative URLs ("//evil.com/...")
 *  - javascript:, data:, vbscript:, file:, etc.
 *  - Any https URL whose hostname is not in the allowlist
 *
 * @param {string} rawUrl
 * @param {string[]} [allowedSuffixes]
 * @returns {boolean}
 */
const isSafeRedirect = (rawUrl, allowedSuffixes = ALLOWED_HOST_SUFFIXES) => {
  if (typeof rawUrl !== 'string') return false;
  const trimmed = rawUrl.trim();
  if (!trimmed) return false;

  // Reject control characters (CRLF injection etc.).
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return false;

  // Reject protocol-relative URLs outright.
  if (trimmed.startsWith('//')) return false;

  // Same-origin path: starts with single "/" followed by non-"/" non-"\".
  if (trimmed.startsWith('/') && !trimmed.startsWith('/\\')) {
    return true;
  }

  let parsed;
  try {
    parsed = new URL(trimmed, window.location.origin);
  } catch {
    return false;
  }

  const proto = parsed.protocol.toLowerCase();
  const host = parsed.hostname.toLowerCase();

  // Permit any protocol for localhost (covers http during dev).
  if (ALWAYS_ALLOWED_DEV_HOSTS.includes(host)) return true;

  // Production: https only, host must match an allowlisted suffix.
  if (proto !== 'https:') return false;
  return allowedSuffixes.some((suffix) => hostnameMatchesSuffix(host, suffix));
};

/**
 * Redirect the browser only if the URL passes the allowlist; otherwise send
 * the user to the safe fallback (default: site root on the current origin).
 *
 * @param {string} rawUrl
 * @param {string} [fallback]
 */
export const safeRedirect = (rawUrl, fallback = '/') => {
  if (isSafeRedirect(rawUrl)) {
    window.location.replace(rawUrl);
    return;
  }
  // eslint-disable-next-line no-console
  console.warn('[safeRedirect] blocked off-allowlist redirect:', rawUrl);
  window.location.replace(fallback);
};

/**
 * Sanitize a URL for use as an anchor href / iframe src. Returns the original
 * URL if it passes the allowlist, otherwise an empty string so the element
 * renders without a navigation target.
 *
 * @param {string} rawUrl
 * @returns {string}
 */
export const sanitizeExternalHref = (rawUrl) => {
  if (!rawUrl) return '';
  return isSafeRedirect(rawUrl) ? rawUrl : '';
};
